import { Box, Button, Divider, Flex } from "@chakra-ui/react";
import BoxHeader from "components/elements/BoxHeader";
import { LoanContext } from "providers/loan";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { useContext, useState, useEffect } from "react";
import FieldsTable from "../../FieldsTable";
import { isPermissionEnabled, permissionConstants, hasPermission } from "utils/permissionHelper";
import { IconAlert } from "components/widgets/IconAlert";
import InputSearch from "components/elements/InputSearch/input-search";
import {
  ApplyFilterTo,
  LoanApplicationToogle,
  LoanApplicationToogleOptions,
} from "../loan-application-toogle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DownloadLoanApplicationButton } from "modules/loan/components/download-loan-application-button";

const LoanFieldsView = ({
  isOpen,
  setEditing,
}: {
  isOpen: boolean;
  setEditing: (editing: boolean) => void;
}) => {
  const { user } = useTenant();
  const { loan } = useContext(LoanContext);

  const [filterText, setFilterText] = useState("");
  const [readOnlyLoan, setReadOnlyLoan] = useState(false);

  const custom_loan_headers =
    user?.company?.company_configurations?.[0]?.ui_prefrences?.fields_view_headers;
  const custom_fields_view_headers =
    user?.company?.company_configurations?.[0]?.ui_prefrences?.fields_view_headers;

  const applicationTerm =
    user?.lender_company?.id === "2e6eae2b-de55-4c49-96c6-ff77740565f1" ||
    user?.lender_company?.id === "59d63802-e33d-4b99-9ddd-e92004455eb3"
      ? "Term Sheet"
      : "Application";

  const checkPermissionsToEditLoan = () => {
    const hasPermissionsEnabled = isPermissionEnabled(user);
    const hasReadOnlyAccess = hasPermission(user, permissionConstants.readOnlyLoans);

    // If user only has read permissions, set loan to read-only mode
    if (hasPermissionsEnabled && hasReadOnlyAccess) {
      setReadOnlyLoan(true);
    }
  };

  useEffect(() => {
    checkPermissionsToEditLoan();
  }, []);

  const [activeTab, setActiveTab] = useState<LoanApplicationToogleOptions>(
    LoanApplicationToogleOptions.Application
  );

  const notUnderwritingFields = loan?.fields?.filter((field: any) => !field.underwriting) || [];
  const underwritingFields = loan?.fields?.filter((field: any) => field.underwriting) || [];

  return (
    <Box>
      <Flex direction="column" gap={4}>
        <Flex alignItems="center" gap={2}>
          <BoxHeader mb="0px" title="Loan Details" />
          {user.type === "lender" && !isOpen && <IconAlert mb="0px" text="Visible to borrowers." />}

          <InputSearch
            size="sm"
            value={filterText}
            onChange={ev => setFilterText(ev.target.value)}
            placeholder="Filter fields..."
            debounceTime={0}
          />

          {(loan?.status.isDraft || user.type === "lender") && (
            <Button
              variant="white"
              isDisabled={readOnlyLoan}
              size="sm"
              onClick={() => setEditing(true)}
            >
              Edit Details
            </Button>
          )}

          <DownloadLoanApplicationButton loanId={loan?.id} />
        </Flex>

        <LoanApplicationToogle
          activeTab={activeTab}
          onChange={setActiveTab}
          applicationTerm={applicationTerm}
        />

        {user.type === "lender" && isOpen && (
          <IconAlert text="Loan details are visible to borrowers." mb="" />
        )}

        <FieldsTable
          columns={isOpen ? 2 : 3}
          fields={notUnderwritingFields
            .filter(field => ApplyFilterTo(user.type, activeTab, field))
            .filter((field: any) => field.label.toLowerCase().includes(filterText.toLowerCase()))}
        />
      </Flex>

      {user.type === "lender" && underwritingFields.length > 0 && (
        <Box>
          <Divider my="20px" bg="gray.50" />
          <Box>
            <Flex alignItems="center" justifyContent="space-between" mb="20px">
              {
                <BoxHeader
                  mb="0px"
                  title={custom_loan_headers ? `${custom_fields_view_headers}` : "Underwriting"}
                />
              }
              {!isOpen && (
                <IconAlert mb="0px" text="Underwriting values are not visible to borrowers." />
              )}
            </Flex>
            {isOpen && <IconAlert text="Underwriting values are not visible to borrowers." />}
            <FieldsTable
              columns={isOpen ? 2 : 3}
              fields={underwritingFields.filter((field: any) =>
                field.label.toLowerCase().includes(filterText.toLowerCase())
              )}
            />
          </Box>
          <Divider my="20px" bg="gray.50" />
        </Box>
      )}
    </Box>
  );
};

export default LoanFieldsView;
